/* Such-Dropdown im Header (Dawn-Basis, HANDOFF.md §5.7).
 *
 * Such-Audit 19.09.2026 — was hier gegenueber Dawn anders ist und warum:
 * - updateSearchForTerm ohne RegExp: 'a+++' warf „Nothing to repeat", und
 *   nach einem Cache-Treffer mit anderer Gross-/Kleinschreibung fand .match()
 *   den alten Begriff nicht mehr (null.length). Weil der Fehler vor
 *   `this.searchTerm = …` flog, blieb das Dropdown bis zum Neuladen stehen.
 *   Der Knopftext kommt jetzt aus einer Vorlage mit [terms].
 * - Cache-Schluessel: alle Leerzeichen normiert (replace ersetzte nur das
 *   erste), ein neuer Abruf bricht den vorigen ab — sonst ueberschrieb eine
 *   spaet eintreffende Antwort fuer „dai" die fuer „daikin".
 * - Tasten ueber event.key und nur am Suchfeld: Pfeiltasten blockierten
 *   vorher auch den Kategorie-Waehler. Escape schliesst nur das Dropdown
 *   (vorher leerte der Browser das Feld) und gelangt nicht bis zu
 *   search-toggle.js, das sonst die ganze mobile Suchzeile zuklappt.
 * - Markierte Option sichtbar (CSS) und in den Sichtbereich gescrollt.
 * - Hoehe aus dem sichtbaren Viewport und der Unterkante des Suchfelds;
 *   '.section-header' gibt es im Theme nicht (ergab NaN).
 * - Kategorie-Waehler: filtert die Produktvorschlaege und sendet
 *   Sammeloptionen (mehrere Produkttypen) als weitere Felder mit.
 * - Mobil: ids der Vorschlaege mit Suffix (data-id-suffix), damit sie nicht
 *   doppelt vorkommen und aria-controls des mobilen Felds trifft.
 * - Null-Treffer: Marken- und Kategorie-Chips aus dem Header-<template>.
 */
class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.idSuffix = this.dataset.idSuffix || '';
    this.kategorie = this.querySelector('select[data-predictive-search-category]');
    this.onViewportResize = this.onViewportResize.bind(this);

    this.setupEventListeners();
    this.syncCategoryInputs();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    if (this.kategorie) {
      this.kategorie.addEventListener('change', this.onCategoryChange.bind(this));
      // Zurueck-Navigation stellt die Auswahl wieder her, die Zusatzfelder
      // muessen dazu passen.
      window.addEventListener('pageshow', () => this.syncCategoryInputs());
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    // Dropdown öffnet ab dem 2. Zeichen (Design: Navigation & Suche v2).
    // Nur das Dropdown schließen — die Eingabe bleibt stehen (close(true)
    // würde das Feld leeren und den getippten Buchstaben "verschlucken").
    if (this.searchTerm.length < 2) {
      this.abortController.abort();
      this.abortController = new AbortController();
      this.close(false);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) {
      event.preventDefault();
      return;
    }

    // Kategorie-Waehler nur mitsenden, wenn er sichtbar ist und etwas
    // gewaehlt hat: Unter 1200 px blendet section-header.css ihn aus — eine
    // vorbelegte Auswahl haette dort unsichtbar weitergefiltert. „Alle
    // Kategorien" soll keinen leeren filter.p.product_type anhaengen.
    if (this.kategorie && (!this.kategorie.value || !this.isCategoryVisible())) {
      const felder = [this.kategorie, ...this.querySelectorAll('[data-predictive-search-category-extra]')];
      felder.forEach((feld) => {
        feld.disabled = true;
      });
      // Das Formular hat seine Daten beim submit-Ereignis schon gelesen.
      setTimeout(() => {
        felder.forEach((feld) => {
          feld.disabled = false;
        });
      });
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (event.target !== this.input) return;
    if (!this.getQuery().length) this.close(true);
  }

  onKeydown(event) {
    // Nur Tasten im Suchfeld selbst — der Kategorie-Waehler und die Knoepfe
    // im Formular behalten ihre eigene Tastaturbedienung.
    if (event.target !== this.input || event.isComposing) return;

    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowDown':
        if (!this.isOpen) {
          if (this.getAttribute('results') === 'true' && this.getQuery().length >= 2) {
            event.preventDefault();
            this.open();
          }
          return;
        }
        // Cursor im Feld nicht bewegen
        event.preventDefault();
        this.switchOption(event.key === 'ArrowUp' ? 'up' : 'down');
        break;
      case 'Enter':
        // Mit markierter Option: diese oeffnen statt das Formular abzusenden.
        if (this.isOpen && this.getSelectedOption()) {
          event.preventDefault();
          this.selectOption();
        }
        break;
      case 'Escape':
        if (this.isOpen || this.hasAttribute('loading')) {
          // preventDefault: Chrome/Safari leeren type=search sonst nativ.
          // stopPropagation: search-toggle.js lauscht am document und
          // wuerde die mobile Suchzeile gleich mit schliessen.
          event.preventDefault();
          event.stopPropagation();
          if (this.hasAttribute('loading')) {
            // Ein Abruf laeuft noch: verwerfen, sonst oeffnet seine Antwort
            // das eben geschlossene Dropdown wieder (renderSearchResults).
            // Was bisher drinsteht, gilt nicht fuer den Begriff im Feld —
            // der naechste Fokus laedt neu, Pfeil-runter zeigt nichts Altes.
            this.abortController.abort();
            this.abortController = new AbortController();
            this.removeAttribute('results');
          }
          this.close(false);
        }
        break;
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.predictiveSearchResults.querySelector('[data-predictive-search-search-for-text]');
    if (!searchForTextElement) return;

    const kategorie = this.getCategory();
    const vorlage = kategorie
      ? searchForTextElement.dataset.vorlageKategorie
      : searchForTextElement.dataset.vorlage;

    if (vorlage) {
      // Ersetzung per Funktion: ein „$&" im Suchbegriff bliebe sonst kein Text.
      // [kategorie] zuerst: Enthielte der Begriff selbst „[kategorie]", traefe
      // die zweite Ersetzung sonst den eingesetzten Suchbegriff.
      searchForTextElement.textContent = vorlage
        .replace('[kategorie]', () => (kategorie ? kategorie.label : ''))
        .replace('[terms]', () => newTerm);
      return;
    }

    // Rueckfall fuer Markup ohne Vorlage: Klartextsuche statt RegExp,
    // unabhaengig von Gross-/Kleinschreibung; mehrdeutig → nichts ersetzen.
    if (!previousTerm) return;
    const text = searchForTextElement.textContent;
    const klein = text.toLowerCase();
    const alt = previousTerm.toLowerCase();
    const index = klein.indexOf(alt);
    if (index === -1 || klein.indexOf(alt, index + 1) !== -1) return;
    searchForTextElement.textContent = text.slice(0, index) + newTerm + text.slice(index + previousTerm.length);
  }

  getVisibleOptions() {
    // offsetParent filtert ausgeblendete Optionen (hidden, Kategorie-Filter)
    return Array.from(this.predictiveSearchResults.querySelectorAll('[role="option"]')).filter(
      (element) => element.offsetParent !== null
    );
  }

  getSelectedOption() {
    return this.predictiveSearchResults.querySelector('[role="option"][aria-selected="true"]');
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.getSelectedOption();
    const allVisibleElements = this.getVisibleOptions();
    if (!allVisibleElements.length) return;
    if (moveUp && !selectedElement) return;

    const selectedElementIndex = allVisibleElements.indexOf(selectedElement);
    let activeElementIndex = 0;

    if (selectedElementIndex !== -1) {
      if (moveUp) {
        activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
      } else {
        activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
      }
    }

    const activeElement = allVisibleElements[activeElementIndex];
    if (activeElement === selectedElement) return;

    if (this.statusElement) this.statusElement.textContent = '';
    if (selectedElement) selectedElement.setAttribute('aria-selected', 'false');
    activeElement.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', activeElement.id);
    this.scrollOptionIntoView(activeElement);
  }

  scrollOptionIntoView(option) {
    // Nur die Liste scrollen, nicht die Seite (scrollIntoView wuerde auch
    // das Fenster bewegen). Die angeheftete Zeile „Alle Ergebnisse" deckt
    // unten einen Streifen ab — den ziehen wir von der Sichtflaeche ab.
    const box = this.predictiveSearchResults;
    const boxRect = box.getBoundingClientRect();
    const rect = option.getBoundingClientRect();
    const fuss = box.querySelector('.predictive-search__search-for-button');
    const fussHoehe = fuss && !fuss.contains(option) ? fuss.offsetHeight : 0;
    const luft = 6;

    if (rect.top < boxRect.top + luft) {
      box.scrollTop -= boxRect.top + luft - rect.top;
    } else if (rect.bottom > boxRect.bottom - fussHoehe - luft) {
      box.scrollTop += rect.bottom - (boxRect.bottom - fussHoehe - luft);
    }
  }

  selectOption() {
    const selectedOption = this.getSelectedOption();
    if (!selectedOption) return;

    const ziel = selectedOption.matches('a, button') ? selectedOption : selectedOption.querySelector('a, button');
    if (ziel) ziel.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.toLowerCase().replace(/\s+/g, ' ');
    this.setLiveRegionLoadingState();

    // Einen noch laufenden Abruf fuer einen aelteren Begriff verwerfen.
    this.abortController.abort();
    this.abortController = new AbortController();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);

    // Bis zu 10 Vorschlaege je Typ: Die Section sortiert Komplettanlagen nach
    // vorn und zeigt 4; der Rest steht fuer den Kategorie-Filter bereit.
    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&resources[type]=product,collection,article,query&resources[limit]=10&resources[limit_scope]=each&section_id=predictive-search`,
      {
        signal: this.abortController.signal,
      }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });

        // Inzwischen weitergetippt: Der neuere Abruf rendert selbst.
        if (queryKey !== this.searchTerm.toLowerCase().replace(/\s+/g, ' ')) {
          searchDeferred?.resolve({ totalCount: 0 });
          return;
        }

        this.renderSearchResults(resultsMarkup);

        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) {
          // Code 20 means the call was aborted
          searchDeferred?.reject(error);
          return;
        }
        searchDeferred?.reject(error);
        this.close();
        throw error;
      });
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.fillEmptyState();
    this.applyIdSuffix();
    this.applyCategory();
    // Knopftext auf den aktuellen Begriff — der Cache-Treffer kann eine
    // andere Schreibweise enthalten.
    this.updateSearchForTerm('', this.searchTerm || this.getQuery());
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    // Kam die Antwort erst, nachdem der Fokus das Suchfeld verlassen hat,
    // bleibt das Dropdown zu (focusout feuert nicht noch einmal).
    if (this.contains(document.activeElement)) {
      this.open();
    } else {
      this.removeAttribute('loading');
    }
  }

  fillEmptyState() {
    const slot = this.predictiveSearchResults.querySelector('[data-predictive-search-empty-slot]');
    if (!slot) return;
    const vorlage = document.getElementById('PredictiveSearchEmptyExtras');
    if (vorlage) {
      slot.replaceWith(vorlage.content.cloneNode(true));
    } else {
      slot.remove();
    }
  }

  applyIdSuffix() {
    if (!this.idSuffix) return;
    const suffix = this.idSuffix;
    const box = this.predictiveSearchResults;

    box.querySelectorAll('[id]').forEach((element) => {
      element.id = `${element.id}${suffix}`;
    });
    ['aria-labelledby', 'aria-describedby'].forEach((attribut) => {
      box.querySelectorAll(`[${attribut}]`).forEach((element) => {
        const ids = element
          .getAttribute(attribut)
          .split(/\s+/)
          .filter(Boolean)
          .map((id) => `${id}${suffix}`);
        element.setAttribute(attribut, ids.join(' '));
      });
    });
  }

  isCategoryVisible() {
    return Boolean(this.kategorie) && this.kategorie.getClientRects().length > 0;
  }

  getCategory() {
    if (!this.kategorie || !this.kategorie.value || !this.isCategoryVisible()) return null;
    const option = this.kategorie.selectedOptions[0];
    if (!option) return null;
    const typen = (option.dataset.typen || option.value)
      .split(';')
      .map((typ) => typ.trim())
      .filter(Boolean);
    return { label: option.textContent.trim(), typen };
  }

  applyCategory() {
    const box = this.predictiveSearchResults;
    const hinweis = box.querySelector('[data-predictive-search-category-hint]');
    // Kein Hinweis-Element = Null-Treffer-Zustand, dort gibt es nichts zu filtern.
    if (!hinweis) return;

    const kategorie = this.getCategory();
    const liste = box.querySelector('[data-predictive-search-products]');
    let produkte = 0;

    if (liste) {
      const max = parseInt(liste.dataset.max, 10) || 4;
      liste.querySelectorAll('[data-product-type]').forEach((eintrag) => {
        const passt = !kategorie || kategorie.typen.includes(eintrag.dataset.productType);
        const zeigen = passt && produkte < max;
        eintrag.hidden = !zeigen;
        if (!zeigen) eintrag.setAttribute('aria-selected', 'false');
        if (zeigen) produkte += 1;
      });
      const gruppe = liste.closest('[data-predictive-search-group]');
      if (gruppe) gruppe.hidden = produkte === 0;
    }

    // Kategorien fuehren aus der gewaehlten Kategorie heraus — bei aktivem
    // Filter weglassen.
    const kategorien = box.querySelector('[data-predictive-search-group="collections"]');
    if (kategorien) kategorien.hidden = Boolean(kategorie);

    hinweis.hidden = !kategorie;
    if (kategorie) {
      const vorlage = produkte > 0 ? hinweis.dataset.vorlage : hinweis.dataset.vorlageLeer;
      hinweis.textContent = (vorlage || '').replace('[kategorie]', () => kategorie.label);
    }

    // Trefferzahl fuer Live-Region und SearchUpdateEvent nachziehen
    const zaehler = box.querySelector('[data-predictive-search-live-region-count-value]');
    if (!zaehler) return;
    if (zaehler.dataset.textOriginal === undefined) zaehler.dataset.textOriginal = zaehler.textContent.trim();
    if (zaehler.dataset.totalOriginal === undefined) zaehler.dataset.totalOriginal = zaehler.dataset.totalResults;

    if (!kategorie) {
      zaehler.textContent = zaehler.dataset.textOriginal;
      zaehler.dataset.totalResults = zaehler.dataset.totalOriginal;
      return;
    }

    const artikel = box.querySelectorAll('[data-predictive-search-group="articles"] [role="option"]').length;
    const anzahl = produkte + artikel;
    let text = hinweis.textContent;
    if (anzahl === 1) {
      text = zaehler.dataset.textOne || text;
    } else if (anzahl > 1 && zaehler.dataset.textOther) {
      text = zaehler.dataset.textOther.replace('[count]', String(anzahl));
    }
    zaehler.textContent = text;
    zaehler.dataset.totalResults = String(anzahl);
  }

  onCategoryChange() {
    this.syncCategoryInputs();
    if (this.getAttribute('results') !== 'true') return;

    const selected = this.getSelectedOption();
    if (selected) selected.setAttribute('aria-selected', 'false');
    this.input.removeAttribute('aria-activedescendant');

    this.applyCategory();
    this.updateSearchForTerm('', this.searchTerm || this.getQuery());
    if (this.isOpen) this.setLiveRegionResults();
  }

  syncCategoryInputs() {
    if (!this.kategorie) return;
    this.querySelectorAll('[data-predictive-search-category-extra]').forEach((feld) => feld.remove());

    const option = this.kategorie.selectedOptions[0];
    if (!option || !option.value) return;
    const typen = (option.dataset.typen || '')
      .split(';')
      .map((typ) => typ.trim())
      .filter((typ) => typ && typ !== this.kategorie.value);

    // Mehrere Werte desselben Filters verknuepft Shopify mit ODER.
    let anker = this.kategorie;
    typen.forEach((typ) => {
      const feld = document.createElement('input');
      feld.type = 'hidden';
      feld.name = this.kategorie.name;
      feld.value = typ;
      feld.setAttribute('data-predictive-search-category-extra', '');
      anker.after(feld);
      anker = feld;
    });
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    // Unterkante des sichtbaren Bereichs (mit Bildschirmtastatur kleiner als
    // innerHeight) minus Oberkante des Dropdowns (0,8 rem unter dem Feld)
    // minus etwas Luft. Mindestens 16 rem, damit es nie zur Linie schrumpft.
    const viewport = window.visualViewport;
    const sichtUnten = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
    const oben = this.getBoundingClientRect().bottom + 8;
    this.resultsMaxHeight = Math.round(Math.max(sichtUnten - oben - 16, 160));
    return this.resultsMaxHeight;
  }

  onViewportResize() {
    if (!this.isOpen) return;
    this.predictiveSearchResults.style.setProperty('--predictive-search-max-height', `${this.getResultsMaxHeight()}px`);
  }

  open() {
    this.predictiveSearchResults.style.setProperty('--predictive-search-max-height', `${this.getResultsMaxHeight()}px`);
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    if (!this.isOpen) {
      window.visualViewport?.addEventListener('resize', this.onViewportResize);
      window.addEventListener('resize', this.onViewportResize);
    }
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.getSelectedOption();

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.removeAttribute('aria-activedescendant');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    // Auch hier, nicht nur in close(): onFormReset ruft closeResults direkt.
    // Blieb isOpen dort true, schluckte das naechste Escape die Taste (die
    // mobile Suchzeile ging nicht zu), Pfeil-runter oeffnete nicht wieder und
    // open() meldete die Resize-Listener nicht erneut an.
    this.isOpen = false;
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.style.removeProperty('--predictive-search-max-height');
    window.visualViewport?.removeEventListener('resize', this.onViewportResize);
    window.removeEventListener('resize', this.onViewportResize);
  }
}

customElements.define('predictive-search', PredictiveSearch);
